const DebouncedTcpSocketConnection = require('./DebouncedTcpSocketConnection');
const { indexOfAll } = require('@/functions/strings');

/**
 * Some messages from Verifone terminals exceed the size of a single TCP data message, so they get split into 2.
 * To cater for this you can use this class which buffers all data received until it sees a CRLF (\r\n) then it emits
 * all the data until then.
 */
class BufferedDebouncedTcpSocketConnection extends DebouncedTcpSocketConnection {
    /**
     * @param {string} name
     * @param {string} host
     * @param {number} port
     * @param {AgentService} agentService
     */
    constructor(name, host, port, agentService) {
        super(name, host, port, agentService);

        /**
         * @type {string}
         */
        this.receivedDataBuffer = '';

        /**
         * @type {string}
         */
        this.messageTerminator = '\r\n';

        this.messageTerminatorRegExp = new RegExp(this.messageTerminator);

        /**
         * A receipt message from a Verifone terminal might contain XML containing a custom footer tag which contains
         * a CRLF. Initially the idea was to change this class so instead of returning a message when it encounters
         * any CRLF, it only returns when it encounters a CRLF at the end of any incoming message.
         * This would work most of the time, but it's possible that a receipt message it too large for 1 TCP message
         * and it gets split at an unfortunate location immediately after the CRLF in the <Footer> tag. This would look
         * like the end of the message as the CRLF is at the end of the first message.
         *
         * Instead to handle this, this is an array of special cases where we won't emit a message if it contains the
         * first string but not the second string. If the first string is encountered, it will keep buffering messages
         * until it can create a message containing both strings.
         */
        this.pairedStrings = [
            [
                '<VoucherDetails>',
                '</VoucherDetails>',
            ],
        ];
    }

    /**
     * @param {{socketName: string, data: string}} data
     */
    _didReceiveData({ data }) {
        // Add the data until the buffer.
        this.receivedDataBuffer += data;

        // If the buffered string contains at least 1 occurrence of the terminator...
        while (this.receivedDataBuffer.length > 0) {
            let message = '';
            let emitted = false;

            let indices = indexOfAll(this.messageTerminatorRegExp, this.receivedDataBuffer);

            let i;
            for (i = 0; i < indices.length; i++) {
                message = this.receivedDataBuffer.substring(0, indices[i] + this.messageTerminator.length);

                // Check if the potential string is valid. Valid meaning it either doesn't contain the start
                // of a "paired string", or it contains both the start and ending.
                // Note that the ending part of the string on its own shouldn't do anything special.
                if (this.isValidMessage(message)) {
                    // Emit the message.
                    emitted = true;

                    if (typeof this.onData === 'function') {
                        this.onData(message, this);
                    }

                    // Remove the message from the buffer.
                    this.receivedDataBuffer = this.receivedDataBuffer.substring(message.length);
                    indices = indices.splice(0, i + 1);

                    break;
                }
            }

            if (!emitted) {
                // Prevent an infinite loop. The buffer contains the message terminator, but we didn't
                // manage to build a valid string even using up the whole buffer. We probably
                // need to wait for more data to arrive.
                break;
            }
        }
    }

    /**
     * Check if the given string contains the start of one of the paired strings but not the end of it. Returns false
     * if it does, otherwise returns true.
     *
     * @param {string} str
     */
    isValidMessage(str) {
        for (let i = 0; i < this.pairedStrings.length; i++) {
            if (str.indexOf(this.pairedStrings[i][0]) !== -1 && str.indexOf(this.pairedStrings[i][1]) === -1) {
                return false;
            }
        }

        return true;
    }
}

module.exports = BufferedDebouncedTcpSocketConnection;
